/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { Constants } from '../../constants';
import { DeviceModel } from '../../device';
import { ErrorTypes, LiveTvError } from '../../error';
import { Locale } from '../../locale/Locale';
import { LocaleKeys } from '../../locale/LocaleKeys';
import { Log } from '../../logging';
import { BrandingUiUtility } from '../../utils';
import { CcbSwitch } from '../../utils/ccb';
import { CcbSwitchName } from '../../utils/ccb/enums';

/*
    Live Tv screen other errors are mapped as a part of LiveTvGeneralErrorMapper.
    Example: adult, unsubscribed of network etc.
*/

export class LiveTvGeneralErrorMapper {
    private static TAG: string = 'LiveTvGeneralErrorMapper';

    private code: string;

    private description: string;

    private message: string;

    private title: string;

    private type: string;

    public constructor() {
        this.code = '';
        this.description = '';
        this.message = '';
        this.title = '';
        this.type = '';
    }

    private setGeneralErrorProperties(
        errorCode: string,
        channelName?: string,
        ratingName?: string,
    ) {
        switch (errorCode) {
            case ErrorTypes.LIVE_TV_CHANNEL_NOT_SUBSCRIBED_ERROR_CODE_V56:
                this.code = errorCode;
                this.description = `${channelName} ${Locale.getString(
                    LocaleKeys.LIVE_TV_CHANNEL_NOT_SUBSCRIBED_MESSAGE,
                )}`;
                break;
            case ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_AUTHORIZED_DEVICE_TYPE_V414:
                this.code = errorCode;
                this.description = Locale.getString(
                    LocaleKeys.LIVE_TV_CANNOT_PLAY_ON_THIS_DEVICE_OSD_MESSAGE,
                );
                break;
            case ErrorTypes.NO_NETWORK_OVERLAY_ERROR_CODE:
                this.code = errorCode;
                this.description = Locale.getString(LocaleKeys.NO_NETWORK_OVERLAY_DESCRIPTION);
                break;
            default:
                this.code = '';
                this.description = Locale.getString(LocaleKeys.LIVE_TV_ADULT_OSD_MESSAGE);
                if (ratingName === Constants.UNKNOWN) {
                    this.description = BrandingUiUtility.formatDescription(
                        Locale.getString(LocaleKeys.LIVE_TV_OSD_PC_RATING_UNKNOWN_MESSAGE),
                        {
                            BUTTON_NAME_SELECT: DeviceModel.isWebOsPlatform()
                                ? Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_LG)
                                : Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_SAMSUNG),
                        },
                    );
                } else if (ratingName?.length && ratingName !== Constants.UNKNOWN) {
                    this.description = BrandingUiUtility.formatDescription(
                        Locale.getString(LocaleKeys.LIVE_TV_OSD_PC_LOCKED_RATING_MESSAGE),
                        {
                            RATING: `$regular-text-highlight: ${ratingName}$`,
                            BUTTON_NAME_SELECT: DeviceModel.isWebOsPlatform()
                                ? Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_LG)
                                : Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_SAMSUNG),
                        },
                    );
                } else {
                    this.description = BrandingUiUtility.formatDescription(
                        Locale.getString(LocaleKeys.LIVE_TV_OSD_PC_ADULT_LOCKED_MESSAGE),
                        {
                            BUTTON_NAME_SELECT: DeviceModel.isWebOsPlatform()
                                ? Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_LG)
                                : Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_SAMSUNG),
                        },
                    );
                }
        }
    }

    public getErrorByCode(
        errorCode: string,
        channelName?: string,
        ratingName?: string,
    ): LiveTvError {
        Log.e(LiveTvGeneralErrorMapper.TAG, 'location error code ', errorCode);
        Diagnostics.error(LiveTvGeneralErrorMapper.TAG, 'location error code ', errorCode);
        this.setGeneralErrorProperties(errorCode, channelName, ratingName);
        return new LiveTvError(this.code, this.message, this.description, this.title, this.type);
    }
}
